import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { VibrationsRecordService } from '@shared/services/records/vibrations-record.service';
import { VibrationsChart } from '@shared/models/charts/VibrationsChart';
import { ControlPointsService } from '@features/control-points/services/control-points-service';
import { ControlPoint } from '@shared/models/controlPoint';
import { EventData } from '@features/vibration-events/models/event-data';
import { FormsModule } from '@angular/forms';
import { AssignedRecords } from '@shared/models/assigned-records';
import { NgSelectModule } from '@ng-select/ng-select';
import { FonctionsService } from '@shared/services/utilities/fonctions.service';
import { MonitoringService } from '@features/monitoring/services/monitoring.service';
import { LocalStorageService } from '@shared/services/utilities/local-storage.service';
import { Site } from '@shared/models/site';
import { debounceTime, Subject } from 'rxjs';

@Component({
  selector: 'app-assign-records-modal',
  imports: [CommonModule, AngularSvgIconModule, MatProgressBarModule, TranslateModule, FormsModule,NgSelectModule],
  templateUrl: './assign-records-modal.component.html',
  styleUrl: './assign-records-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssignRecordsModalComponent implements OnInit {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  @Input() siteId: number;
  sortColumn: string = '';
  sortOrder: boolean = true;
  allRecords: VibrationsChart[];
  controlPoints: ControlPoint[] = [];
  events: EventData[] = [];
  selectedRecordIds: number[] = [];
  selectedEventId: number;
  selectedControlPointId: number;
  selectedSiteId: number;
  allSites: Site[] = [];
  // Filter properties
  serialNumberFilter: string = '';
  startDateFilter: string = '';
  endDateFilter: string = '';
  private currentPage = 1;  
  private isLoading = false;
  devices: any[] = [];
  errorMessage: string = '';
  noteFilter = '';
  protected noteSearch$ = new Subject<string>();
   constructor(
      public dialogRef: MatDialogRef<AssignRecordsModalComponent>,
      private recordSevice: VibrationsRecordService,
      @Inject(MAT_DIALOG_DATA) public data: any,
      private cdRef: ChangeDetectorRef,
      private controlPointsService: ControlPointsService,
      private fonctionsService: FonctionsService,
      private translate: TranslateService,
      private monitoringService: MonitoringService,
      private localStorageService: LocalStorageService,
    ) {}
  ngAfterViewInit() {
    this.scrollContainer.nativeElement.addEventListener('scroll', this.onScroll.bind(this));
  }
  ngOnInit(): void {   
    this.siteId = this.data.siteId;
    this.events = this.data.events;
    this.allSites = this.localStorageService.getItem('allSites') || [];
    this.selectedEventId = this.data.eventId;
    this.selectedControlPointId = this.data.controlPointId;
    this.loadRecords();
    this.getDevicesNames();
    this.noteSearch$.pipe(debounceTime(500)).subscribe(() => this.onFilterChange());
    this.cdRef.detectChanges();
  }
  getDevicesNames(){
    this.monitoringService.getMonitoringDeviceNames().subscribe({
      next: (devicesNames) => {
        this.devices = devicesNames;
        this.cdRef.detectChanges();
      }
    });
  }
  loadRecords(pageNumber: number = 1, append: boolean = false): void {
    if (this.isLoading) return;
    this.isLoading = true;
    const serialNumber = this.serialNumberFilter || null;
    const startDate = this.startDateFilter || null;
    const endDate = this.endDateFilter || null;
    const note = this.noteFilter || null;
    this.recordSevice.GetRecords(
      this.siteId, 
      this.selectedEventId, 
      null, 
      this.selectedControlPointId, 
      serialNumber, 
      pageNumber, 
      10, 
      false, 
      false, 
      true,
      null,
      null,
      startDate,
      endDate,
      false,
      note
    ).subscribe((records) => {
      if (append) {
        this.allRecords = [...this.allRecords, ...records.data.records];
      } else {
        this.allRecords = records.data.records;
      }
      this.cdRef.detectChanges();
      this.isLoading = false;
    });
  }
  onScroll(event: Event): void {
    const element = this.scrollContainer.nativeElement;
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 100;
    if (atBottom && !this.isLoading) {
      this.currentPage++;
      this.loadRecords(this.currentPage, true);
    }
  }
  public closeDialog(): void {
    this.dialogRef.close(false);
  }
  confirm() {
    if (this.selectedRecordIds.length === 0 || (this.selectedEventId === undefined && this.selectedControlPointId === undefined)) {
      this.errorMessage = this.translate.instant('ERRORS.SELECT_RECORDS_ERROR');
      this.cdRef.detectChanges();
      return;
    }
  
    let data: AssignedRecords = {
      recordIds: this.selectedRecordIds,
      eventId: this.selectedEventId,
      controlPointId: this.selectedControlPointId,
      siteId: this.selectedSiteId ?? this.siteId,
      controlPointName: this.getControlPointName(this.selectedControlPointId),
    };
    this.recordSevice.assignRecords(this.data.siteId, data).subscribe({
      next: () => {
        this.fonctionsService.launch_toast(true, this.translate.instant('RECORD.ASSIGNMENT_SUCCESS'));
        this.dialogRef.close(true);
      },
      error: () => {
        this.fonctionsService.launch_toast(false, this.translate.instant('RECORD.ASSIGNMENT_ERROR'));
      }
    });
  }


  sortTable(column: string): void {
    if (this.sortColumn === column) {
      this.sortOrder = !this.sortOrder;
    } else {
      this.sortColumn = column;
      this.sortOrder = true;
    }
      this.allRecords.sort((a, b) => {
      let valA: any, valB: any;
        switch (column) {
        case 'filename':
          valA = a.recordMetadata.fileName?.toLowerCase() || '';
          valB = b.recordMetadata.fileName?.toLowerCase() || '';
          break;
        case 'date':
          valA = new Date(a.recordMetadata.timestamp);
          valB = new Date(b.recordMetadata.timestamp);
          break;
        default:
          return 0;
      }
        if (column === 'date') {
        return this.sortOrder ? valA.getTime() - valB.getTime() : valB.getTime() - valA.getTime();
      }

        return this.sortOrder ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1);
    });
  }
  findMaxAbsValue(min: number, max: number) {
    return Math.abs(max) > Math.abs(min) ? max : Math.abs(min);
  }

  onSiteChange(siteId: number): void {
    this.selectedControlPointId = undefined;
    this.controlPoints = [];
    if (siteId) {
      this.controlPointsService.getControlPointsBySiteId(siteId, true).subscribe(response => {
        this.controlPoints = response;
        this.cdRef.detectChanges();
      });
    }
  }
  onCheckboxChange(recordId: number, event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    if (isChecked) {
      if (!this.selectedRecordIds.includes(recordId)) {
        this.selectedRecordIds = [...this.selectedRecordIds, recordId];
      }
    } else {
      this.selectedRecordIds = this.selectedRecordIds.filter(id => id !== recordId);
    }
  }
  getControlPointName(controlPointId: number): string {    
    const controlPoint = this.controlPoints?.find(cp => cp.controlPointId === controlPointId);
    if (this.controlPoints == undefined) {
      return '';
    }
    return controlPoint ? controlPoint.name : '';
  }

  getEvent(eventId: number): EventData {
    if (this.events.length == 0) return null;    
    return this.events.find(cp => cp.id === eventId);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    const element = this.scrollContainer.nativeElement;
    element.scrollTop = 0;
    this.loadRecords();
  }
    public closeErrorMessage(): void {
    this.errorMessage = '';
    this.cdRef.detectChanges();
  }
}
